import {
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { history, useAccess, useIntl, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { outLogin } from '@/services/auth/api';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const { clearMerchant } = useModel('merchant');

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await outLogin();
    // Clear all tokens from localStorage
    localStorage.removeItem('aimerpay_token');
    localStorage.removeItem('aimerpay_refresh_token');
    localStorage.removeItem('aimerpay_token_expires');
    // Clear merchant selection and state
    clearMerchant();
    // Clear impersonation state (SuperAdmin acting-as-owner)
    sessionStorage.removeItem('aimerpay_acting_as_merchant_owner');
    sessionStorage.removeItem('aimerpay_acting_as_merchants_cache');

    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace(`/user/login?${searchParams.toString()}`);
    }
  };
  const { styles } = useStyles();
  const intl = useIntl();
  const access = useAccess();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s: typeof initialState) => ({
          ...s,
          currentUser: undefined,
        }));
      });
      loginOut();
      return;
    }
    if (key === 'admin-invitations') {
      history.push('/admin/invitations');
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems: MenuProps['items'] = [
    ...(access.canViewSettings
      ? [
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: intl.formatMessage({
              id: 'menu.account.settings',
              defaultMessage: 'Account Settings',
            }),
          },
        ]
      : []),
    ...(access.canManageStaff
      ? [
          {
            key: 'staff-management',
            icon: <TeamOutlined />,
            label: intl.formatMessage({
              id: 'menu.account.staff-management',
              defaultMessage: 'Staff Management',
            }),
          },
        ]
      : []),
    ...(access.isSuperAdmin && !access.inMerchantMode
      ? [
          {
            key: 'admin-invitations',
            icon: <UserAddOutlined />,
            label: intl.formatMessage({
              id: 'menu.account.adminInvitations',
              defaultMessage: 'Admin Invitations',
            }),
          },
        ]
      : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({
        id: 'menu.account.logout',
        defaultMessage: 'Logout',
      }),
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
